import * as Yup from 'yup';
import moment from 'moment';
import {
  AGREEMENT_TYPE,
  ACCEPTED_FILE_TYPES,
  ACTING_AS,
  MAX_FILES_COUNT,
  MAX_SIZE_IN_MB,
  THIRD_PARTY_REGISTRATION,
} from './constants';
import { getCountryCode } from '../../../helpers/common';

const MIN_PHONE_DIGITS = 8;
const MAX_PHONE_DIGITS = 10;
const VAT_NUMBER_REGEX = /^(20|21|22)[0-9]{12}2$/;
const TIN_NUMBER_REGEX = /^(20|21|22)[0-9]{8}$/;
const AGREEMENT_DATE_FORMATS = ['YYYY-MM-DD', 'DD-MM-YYYY'];

const ALLOWED_EXTENSIONS = ACCEPTED_FILE_TYPES.split(',').map((ext) =>
  ext.trim().toLowerCase().replace('.', ''),
);

const isValidPhoneLength = (value) => {
  if (!value) {
    return false;
  }
  const { withoutCountryCode } = getCountryCode(value);
  const digits = (withoutCountryCode ?? '').replace(/\D/g, '');
  return digits.length >= MIN_PHONE_DIGITS && digits.length <= MAX_PHONE_DIGITS;
};

const fileSizeValidation = (value, maxSizeInMb) => {
  const oversizedFiles = value?.filter(
    (file) => file.size > maxSizeInMb * 1024 * 1024,
  );
  return oversizedFiles?.length <= 0;
};

const fileCountValidation = (value, maxCount) => {
  return value?.length <= maxCount;
};

const fileTypeValidation = (value) => {
  if (!value || value.length === 0) {
    return true;
  }
  return value.every((file) => {
    const ext =
      file.name?.split('.').pop()?.toLowerCase() ||
      file.url?.split('.').pop()?.toLowerCase();
    return ALLOWED_EXTENSIONS.includes(ext);
  });
};

const parseAgreementDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = moment(value, AGREEMENT_DATE_FORMATS, true);
  return parsedDate.isValid() ? parsedDate : null;
};

const isDateBeforeToday = (value) => {
  const parsedDate = parseAgreementDate(value);
  return parsedDate
    ? parsedDate.isBefore(moment().startOf('day'), 'day')
    : false;
};

const isDateBefore = (value, minValue) => {
  const parsedDate = parseAgreementDate(value);
  const parsedMinDate = parseAgreementDate(minValue);

  if (!parsedDate || !parsedMinDate) {
    return false;
  }

  return parsedDate.isBefore(parsedMinDate, 'day');
};

const shouldUseThirdPartyVatNumber = (values, actor) => {
  if (actor === ACTING_AS.SELLER) {
    return (
      values.thirdPartyRegistered === THIRD_PARTY_REGISTRATION.VAT_REGISTERED
    );
  }

  return Boolean(values.thirdPartyVatNumber) || !values.thirdPartyTinNumber;
};

const shouldUseThirdPartyTinNumber = (values, actor) => {
  if (actor === ACTING_AS.SELLER) {
    return (
      values.thirdPartyRegistered ===
      THIRD_PARTY_REGISTRATION.NOT_VAT_REGISTERED
    );
  }

  return !shouldUseThirdPartyVatNumber(values, actor);
};

export const addNewBillingAgreementValidationSchema = ({
  t,
  createAs,
  actingAs,
  agreementType,
  isAmendment = false,
  initialValues = {},
}) => {
  const actor = createAs ?? actingAs;
  const validationObject = Yup.object().shape({
    sellerName: Yup.string()
      .required(t('special_billing.name-is-required'))
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      ),
    sellerPhoneNumber: Yup.string()
      .required(t('special_billing.seller-phone-number-is-required'))
      .test(
        'phone number',
        t('special_billing.seller-phone-phone-number-is-invalid'),
        (value) => isValidPhoneLength(value ?? ''),
      ),
    sellerVatNumber: Yup.string()
      .matches(VAT_NUMBER_REGEX, t('special_billing.vat_format'))
      .length(15, t('special_billing.vat-number-exact-length'))
      .required(t('special_billing.seller-vat-is-required')),
    sellerEmail: Yup.string()
      .email(t('special_billing.seller-email-is-invalid'))
      .min(5, t('special_billing.seller-email-min-length'))
      .max(50, t('special_billing.seller-email-max-length'))
      .required(t('special_billing.seller-email-is-required')),

    note: Yup.string().max(500, t('special_billing.note-max-length')),
    agreementStartDate: Yup.string()
      .nullable()
      .test(
        'agreement-start-date-not-in-past',
        t('special_billing.agreement-start-date-not-in-past'),
        (value) => {
          return !isDateBeforeToday(value);
        },
      )
      .test(
        'agreement-start-date-amendment-rule',
        t('special_billing.agreement-start-date-cannot-be-in-past'),
        (value) => {
          if (
            agreementType !== AGREEMENT_TYPE.THIRD ||
            !isAmendment ||
            !value ||
            value === initialValues.agreementStartDate
          ) {
            return true;
          }

          return !isDateBeforeToday(value);
        },
      )
      .required(t('special_billing.agreement-start-date-is-required')),
    agreementEndDate: Yup.string()
      .nullable()
      .test(
        'agreement-end-date-after-start-date',
        t('special_billing.agreement-end-date-must-be-after-start-date'),
        function (value) {
          if (!value || !this.parent.agreementStartDate) {
            return true;
          }

          return !isDateBefore(value, this.parent.agreementStartDate);
        },
      )
      .test(
        'agreement-end-date-not-in-past',
        t('special_billing.agreement-end-date-cannot-be-in-past'),
        (value) => {
          if (agreementType !== AGREEMENT_TYPE.THIRD || !value) {
            return true;
          }

          return !isDateBeforeToday(value);
        },
      )
      .test(
        'agreement-end-date-amendment-rule',
        t(
          'special_billing.agreement-end-date-cannot-be-before-existing-end-date',
        ),
        (value) => {
          if (
            agreementType !== AGREEMENT_TYPE.THIRD ||
            !isAmendment ||
            !value ||
            !initialValues.agreementEndDate
          ) {
            return true;
          }

          return !isDateBefore(value, initialValues.agreementEndDate);
        },
      )
      .required(t('special_billing.agreement-end-date-is-required')),
    poaDocument: Yup.mixed()
      .test(
        'fileSize',
        t('special_billing.agreement_document_file_size_validation', {
          size: MAX_SIZE_IN_MB,
        }),
        (value) =>
          value && value.length
            ? fileSizeValidation(value, MAX_SIZE_IN_MB)
            : true,
      )
      .test(
        'fileCount',
        t('special_billing.agreement_document_files_count_validation', {
          count: MAX_FILES_COUNT,
        }),
        (value) =>
          value && value.length
            ? fileCountValidation(value, MAX_FILES_COUNT)
            : true,
      )
      .test(
        'fileType',
        t('special_billing.agreement_document_file_type_validation'),
        (value) => (value && value.length ? fileTypeValidation(value) : true),
      ),
    agreementDocument: Yup.mixed()
      .test(
        'fileRequired',
        t('special_billing.agreement_document_required'),
        (value) => value && value.length > 0,
      )
      .test(
        'fileSize',
        t('special_billing.agreement_document_file_size_validation', {
          size: MAX_SIZE_IN_MB,
        }),
        (value) => fileSizeValidation(value, MAX_SIZE_IN_MB),
      )
      .test(
        'fileCount',
        t('special_billing.agreement_document_files_count_validation', {
          count: MAX_FILES_COUNT,
        }),
        (value) => fileCountValidation(value, MAX_FILES_COUNT),
      )
      .test(
        'fileType',
        t('special_billing.agreement_document_file_type_validation'),
        (value) => fileTypeValidation(value),
      ),
  });

  const buyerValidation = Yup.object().shape({
    buyerName: Yup.string()
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.name-is-required')),
    buyerPhoneNumber: Yup.string()
      .required(t('special_billing.buyer-phone-number-is-required'))
      .test(
        'phone number',
        t('special_billing.buyer-phone-phone-number-is-invalid'),
        (value) => isValidPhoneLength(value ?? ''),
      ),
    buyerVatNumber: Yup.string()
      .matches(VAT_NUMBER_REGEX, t('special_billing.vat_format'))
      .length(15, t('special_billing.vat-number-exact-length'))
      .required(t('special_billing.buyer-vat-is-required')),
    buyerEmail: Yup.string()
      .email(t('special_billing.buyer-email-is-invalid'))
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.buyer-email-is-required')),
  });

  const thirdValidation = Yup.object().shape({
    thirdPartyRegistered:
      actor === ACTING_AS.SELLER
        ? Yup.string()
            .nullable()
            .required(t('special_billing.select_yes_or_no'))
        : Yup.string().nullable(),
    thirdPartyName: Yup.string()
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.name-is-required')),
    thirdPartyPhoneNumber: Yup.string()
      .required(t('special_billing.thirdParty-phone-number-is-required'))
      .test(
        'phone number',
        t('special_billing.thirdParty-phone-phone-number-is-invalid'),
        (value) => isValidPhoneLength(value ?? ''),
      ),
    thirdPartyVatNumber: Yup.string().test(
      'third-party-vat-required-and-valid',
      t('special_billing.thirdParty-vat-is-required'),
      function (value) {
        if (!shouldUseThirdPartyVatNumber(this.parent, actor)) {
          return true;
        }

        if (!value) {
          return this.createError({
            message: t('special_billing.thirdParty-vat-is-required'),
          });
        }

        if (value.length !== 15) {
          return this.createError({
            message: t('special_billing.vat-number-exact-length'),
          });
        }

        if (!VAT_NUMBER_REGEX.test(value)) {
          return this.createError({
            message: t('special_billing.vat_format'),
          });
        }

        return true;
      },
    ),
    thirdPartyTinNumber: Yup.string().test(
      'third-party-tin-required-and-valid',
      t('special_billing.thirdParty-tin-is-required'),
      function (value) {
        if (!shouldUseThirdPartyTinNumber(this.parent, actor)) {
          return true;
        }

        if (!value) {
          return this.createError({
            message: t('special_billing.thirdParty-tin-is-required'),
          });
        }

        if (value.length !== 10) {
          return this.createError({
            message: t('special_billing.thirdParty-tin-must-be-ten-digits'),
          });
        }

        if (!TIN_NUMBER_REGEX.test(value)) {
          return this.createError({
            message: t('special_billing.thirdParty-tin-invalid-format'),
          });
        }

        return true;
      },
    ),
    thirdPartyEmail: Yup.string()
      .email(t('special_billing.thirdParty-email-is-invalid'))
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.thirdParty-email-is-required')),
  });

  if (agreementType === AGREEMENT_TYPE.SELF) {
    return validationObject.concat(buyerValidation);
  } else if (agreementType === AGREEMENT_TYPE.THIRD) {
    return validationObject.concat(thirdValidation);
  }
  return validationObject;
};

export const reviewBillingAgreementValidationSchema = ({
  t,
  createAs,
  actingAs,
  agreementType,
  isAmendment = false,
  initialValues = {},
}) => {
  const actor = createAs ?? actingAs;
  const validationObject = Yup.object()
    .concat(
      addNewBillingAgreementValidationSchema({
        t,
        createAs,
        actingAs,
        agreementType,
        isAmendment,
        initialValues,
      }),
    )
    .shape({
      agreementStartDate: Yup.string(),
    });

  const buyerValidation = Yup.object().shape({
    buyerName: Yup.string()
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.name-is-required')),
    buyerPhoneNumber: Yup.string()
      .required(t('special_billing.buyer-phone-number-is-required'))
      .test(
        'phone number',
        t('special_billing.buyer-phone-phone-number-is-invalid'),
        (value) => isValidPhoneLength(value ?? ''),
      ),
    buyerVatNumber: Yup.string()
      .matches(VAT_NUMBER_REGEX, t('special_billing.vat_format'))
      .length(15, t('special_billing.vat-number-exact-length'))
      .required(t('special_billing.buyer-vat-is-required')),
    buyerEmail: Yup.string()
      .email(t('special_billing.buyer-email-is-invalid'))
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.buyer-email-is-required')),
  });

  const thirdValidation = Yup.object().shape({
    thirdPartyRegistered:
      actor === ACTING_AS.SELLER
        ? Yup.string()
            .nullable()
            .required(t('special_billing.select_yes_or_no'))
        : Yup.string().nullable(),
    thirdPartyName: Yup.string()
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.name-is-required')),
    thirdPartyPhoneNumber: Yup.string()
      .required(t('special_billing.thirdParty-phone-number-is-required'))
      .test(
        'phone number',
        t('special_billing.thirdParty-phone-phone-number-is-invalid'),
        (value) => isValidPhoneLength(value ?? ''),
      ),
    thirdPartyVatNumber: Yup.string().test(
      'third-party-vat-required-and-valid',
      t('special_billing.thirdParty-vat-is-required'),
      function (value) {
        if (!shouldUseThirdPartyVatNumber(this.parent, actor)) {
          return true;
        }

        if (!value) {
          return this.createError({
            message: t('special_billing.thirdParty-vat-is-required'),
          });
        }

        if (value.length !== 15) {
          return this.createError({
            message: t('special_billing.vat-number-exact-length'),
          });
        }

        if (!VAT_NUMBER_REGEX.test(value)) {
          return this.createError({
            message: t('special_billing.vat_format'),
          });
        }

        return true;
      },
    ),
    thirdPartyTinNumber: Yup.string().test(
      'third-party-tin-required-and-valid',
      t('special_billing.thirdParty-tin-is-required'),
      function (value) {
        if (!shouldUseThirdPartyTinNumber(this.parent, actor)) {
          return true;
        }

        if (!value) {
          return this.createError({
            message: t('special_billing.thirdParty-tin-is-required'),
          });
        }

        if (value.length !== 10) {
          return this.createError({
            message: t('special_billing.thirdParty-tin-must-be-ten-digits'),
          });
        }

        if (!TIN_NUMBER_REGEX.test(value)) {
          return this.createError({
            message: t('special_billing.thirdParty-tin-invalid-format'),
          });
        }

        return true;
      },
    ),
    thirdPartyEmail: Yup.string()
      .email(t('special_billing.thirdParty-email-is-invalid'))
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.thirdParty-email-is-required')),
  });

  if (agreementType === AGREEMENT_TYPE.SELF) {
    return validationObject.concat(buyerValidation);
  } else if (agreementType === AGREEMENT_TYPE.THIRD) {
    return validationObject.concat(thirdValidation);
  }
  return validationObject;
};

export const amendBillingAgreementValidationSchema = ({ t }) => {
  return Yup.object().shape({
    sellerPhoneNumber: Yup.string()
      .required(t('special_billing.seller-phone-number-is-required'))
      .test(
        'phone number',
        t('special_billing.seller-phone-phone-number-is-invalid'),
        (value) => isValidPhoneLength(value ?? ''),
      ),
    sellerEmail: Yup.string()
      .email(t('special_billing.seller-email-is-invalid'))
      .min(5, t('special_billing.seller-email-min-length'))
      .max(50, t('special_billing.seller-email-max-length'))
      .required(t('special_billing.seller-email-is-required')),
    buyerName: Yup.string()
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.buyer-name-is-required')),
    buyerPhoneNumber: Yup.string()
      .required(t('special_billing.buyer-phone-number-is-required'))
      .test(
        'phone number',
        t('special_billing.buyer-phone-phone-number-is-invalid'),
        (value) => isValidPhoneLength(value ?? ''),
      ),
    buyerVatNumber: Yup.string()
      .matches(/^(20|21|22)[0-9]{12}2$/, t('special_billing.vat_format'))
      .length(15, t('special_billing.vat-number-exact-length'))
      .required(t('special_billing.buyer-vat-is-required')),
    buyerEmail: Yup.string()
      .email(t('special_billing.buyer-email-is-invalid'))
      .min(
        3,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .max(
        50,
        t('special_billing.entry_between_characters', { min: 3, max: 50 }),
      )
      .required(t('special_billing.buyer-email-is-required')),
    note: Yup.string()
      .min(8, t('special_billing.note-min-length'))
      .max(500, t('special_billing.note-max-length'))
      .required(t('special_billing.note-is-required')),
    agreementStartDate: Yup.string()
      .nullable()
      .required(t('special_billing.agreement-start-date-is-required')),
    agreementEndDate: Yup.string()
      .nullable()
      .required(t('special_billing.agreement-end-date-is-required')),
    poaDocument: Yup.mixed()
      .test(
        'fileSize',
        t('special_billing.agreement_document_file_size_validation', {
          size: MAX_SIZE_IN_MB,
        }),
        (value) =>
          value && value.length
            ? fileSizeValidation(value, MAX_SIZE_IN_MB)
            : true,
      )
      .test(
        'fileCount',
        t('special_billing.agreement_document_files_count_validation', {
          count: MAX_FILES_COUNT,
        }),
        (value) =>
          value && value.length
            ? fileCountValidation(value, MAX_FILES_COUNT)
            : true,
      )
      .test(
        'fileType',
        t('special_billing.agreement_document_file_type_validation'),
        (value) => (value && value.length ? fileTypeValidation(value) : true),
      ),
    agreementDocument: Yup.mixed()
      .test(
        'fileRequired',
        t('special_billing.agreement_document_required'),
        (value) => value && value.length > 0,
      )
      .test(
        'fileSize',
        t('special_billing.agreement_document_file_size_validation', {
          size: MAX_SIZE_IN_MB,
        }),
        (value) => fileSizeValidation(value, MAX_SIZE_IN_MB),
      )
      .test(
        'fileCount',
        t('special_billing.agreement_document_files_count_validation', {
          count: MAX_FILES_COUNT,
        }),
        (value) => fileCountValidation(value, MAX_FILES_COUNT),
      )
      .test(
        'fileType',
        t('special_billing.agreement_document_file_type_validation'),
        (value) => fileTypeValidation(value),
      ),
    termsAndConditions: Yup.boolean().oneOf(
      [true],
      t('special_billing.terms-and-conditions-is-required'),
    ),
  });
};
